// Libs
import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  XMarkIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

// Local
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templates: ExistingEmailTemplate[];
  onCopyTemplate: (template: ExistingEmailTemplate) => void;
  loading?: boolean;
}

export default function CopyTemplateDialog({
  isOpen,
  onClose,
  templates,
  onCopyTemplate,
  loading = false,
}: Props) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // Reset selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId("");
      setSearchTerm("");
    }
  }, [isOpen]);

  // Filter templates based on search
  const filteredTemplates = templates.filter((template) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(search) ||
      template.subject.toLowerCase().includes(search) ||
      template.description?.toLowerCase().includes(search)
    );
  });

  // Sort templates by most recent first
  const sortedTemplates = filteredTemplates.sort(
    (a, b) =>
      b.timestampLastModified.toMillis() - a.timestampLastModified.toMillis(),
  );

  const handleCopy = () => {
    if (selectedTemplate) {
      onCopyTemplate(selectedTemplate);
      onClose();
    }
  };

  const getStatusColor = (status: EmailTemplateStatus) => {
    switch (status) {
      case EmailTemplateStatus.ACTIVE:
        return "bg-green-100 text-green-800";
      case EmailTemplateStatus.DRAFT:
        return "bg-yellow-100 text-yellow-800";
      case EmailTemplateStatus.ARCHIVED:
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return timestamp.toDate().toLocaleDateString();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="mb-4 flex items-center justify-between text-lg font-medium leading-6 text-gray-900"
                >
                  <span>Copy from Existing Template</span>
                  <button
                    onClick={onClose}
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Select an existing template to copy its content as a
                    starting point for your new template.
                  </p>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Templates List */}
                <div className="mb-6 max-h-96 overflow-y-auto rounded-md border border-gray-200">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-gray-500">Loading templates...</p>
                    </div>
                  ) : sortedTemplates.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      {searchTerm
                        ? "No templates match your search."
                        : "No templates available."}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {sortedTemplates.map((template) => (
                        <div
                          key={template.id}
                          className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 ${
                            selectedTemplateId === template.id
                              ? "bg-blue-50"
                              : ""
                          }`}
                          onClick={() => setSelectedTemplateId(template.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="truncate font-medium text-gray-900">
                                  {template.name}
                                </h4>
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(template.status)}`}
                                >
                                  {template.status}
                                </span>
                              </div>
                              <p className="mt-1 truncate text-sm text-gray-600">
                                Subject: {template.subject}
                              </p>
                              {template.description && (
                                <p className="mt-1 truncate text-sm text-gray-500">
                                  {template.description}
                                </p>
                              )}
                              <div className="mt-2 text-xs text-gray-400">
                                Modified{" "}
                                {formatDate(template.timestampLastModified)}
                              </div>
                            </div>
                            <div className="ml-3 flex-shrink-0">
                              {selectedTemplateId === template.id && (
                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                                  <CheckIcon className="h-3 w-3 text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Template Preview */}
                {selectedTemplate && (
                  <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-2 font-medium text-blue-900">
                      Selected Template Preview
                    </h4>
                    <div className="text-sm text-blue-800">
                      <p>
                        <strong>Name:</strong> {selectedTemplate.name}
                      </p>
                      <p>
                        <strong>Subject:</strong> {selectedTemplate.subject}
                      </p>
                      {selectedTemplate.description && (
                        <p>
                          <strong>Description:</strong>{" "}
                          {selectedTemplate.description}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <BaseButtonSecondary onClick={onClose}>
                    Cancel
                  </BaseButtonSecondary>
                  <BaseButtonPrimary
                    onClick={handleCopy}
                    disabled={!selectedTemplate}
                  >
                    Copy Template
                  </BaseButtonPrimary>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
